import { path } from 'ramda';
import { UserAttributes } from '../services/auth/types';
import { Apartment, Status, MainOwner, ClientContactInfo, BeforeLoginApartment } from '../types/apartments';
import { ApartmentClientInformationFormData } from '../types/forms/createApartment';
import { WaterFeeBasis, WaterFeeBasisOptions } from '../types/propValues/apartments';
import { IndexedUsersState } from '../types/states';
import { UserInterface, User, isUserWithContactInfo } from '../types/users';
const showWaterFee = (basis: WaterFeeBasis) => basis === WaterFeeBasisOptions.perTenant ||
    basis === WaterFeeBasisOptions.metered ||
    basis === WaterFeeBasisOptions.perApartment;
const hasOtherOwnerDataIfNeeded = (formData: ApartmentClientInformationFormData) => {
    if (path(['hasOtherOwners'], formData) === 'true') {
        return (path(['otherOwners', 0, 'name'], formData) &&
            path(['otherOwners', 0, 'email'], formData));
    }
    else {
        return true;
    }
};
/** also checking commission statuses, so can notify to admin if the showings are old before marketing */
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
const notReservableApartmentStatus: string = 'created';
const apartmentNotReservable = (status: Status) => status === notReservableApartmentStatus;
/** filtering to return apartmentIdArray of apartments that do not have status of
 * created | contract_rent | tenant
 */
const getReservableApartmentIds = (apartments: Apartment[]) => {
    let apartmentIdArray: string[] = [];
    apartments.forEach((apartment: Apartment) => {
        // if metadata !showings, then no need to fetch backend
        if (apartment.status !== notReservableApartmentStatus &&
            apartment.metadata.showings) {
            apartmentIdArray = [...apartmentIdArray, apartment.apartmentId];
        }
    });
    return apartmentIdArray;
};
const getConfirmedApartments = (apartments: Apartment[]) => apartments.filter(({ metadata }: any) => metadata.contract_confirmed === true);
interface ApartmentAddressProps {
    streetAddress: string;
    streetNumber?: number;
    buildingLetter?: string;
    apartmentNumber?: number;
}
const generateApartmentAddress = ({ streetAddress, buildingLetter, apartmentNumber, streetNumber }: ApartmentAddressProps) => {
    const addressParts = [streetAddress];
    if (streetNumber) {
        addressParts.push(streetNumber.toString());
    }
    if (buildingLetter) {
        addressParts.push(buildingLetter);
    }
    if (apartmentNumber) {
        addressParts.push(`${apartmentNumber}`);
    }
    return addressParts.join(' ');
};
const replaceMainOwnerDataFromUserObject = (mainOwner: MainOwner, user: UserInterface): MainOwner => ({
    ...mainOwner,
    email: user.loginEmail,
    name: user.data?.name || '',
    phoneNumber: user.data?.phoneNumber || '',
    language: (user.data?.language || '') as any
});
const getApartmentOwnerEmail = ({ userId, client }: Apartment, users: IndexedUsersState): string | undefined => {
    const user = users[userId] || undefined;
    if (isUserWithContactInfo(user)) {
        return user.loginEmail;
    }
    else {
        return path<string>(['mainOwner', 'email'], client);
    }
};
const getApartmentOwnerPhone = ({ userId, client }: Apartment, users: IndexedUsersState): string | undefined => {
    const user = users[userId] || undefined;
    if (isUserWithContactInfo(user) && user?.data?.phoneNumber) {
        return user.data.phoneNumber;
    }
    else {
        return client?.mainOwner?.phoneNumber;
    }
};
interface GetClientContactInfoProps {
    apartment: Apartment | undefined;
    user: User | undefined;
    userAttributes: UserAttributes | undefined;
}
const getClientContactInfo = ({ apartment, user, userAttributes }: GetClientContactInfoProps): ClientContactInfo | undefined => {
    let clientContactInfo: ClientContactInfo | undefined = undefined;
    if (apartment) {
        if (user &&
            isUserWithContactInfo(user) &&
            user.data?.name &&
            user.data?.phoneNumber) {
            // client info comes from users database
            clientContactInfo = {
                name: user.data?.name,
                phoneNumber: user.data?.phoneNumber
            };
        }
        else if (userAttributes?.sub !== apartment.userId &&
            apartment.client?.mainOwner?.name &&
            apartment.client?.mainOwner?.phoneNumber) {
            // client info comes from old apartment style
            clientContactInfo = {
                name: apartment.client?.mainOwner?.name,
                phoneNumber: apartment.client?.mainOwner?.phoneNumber
            };
        }
        else if (userAttributes?.sub === apartment.userId &&
            userAttributes?.name &&
            userAttributes?.phone_number) {
            // client info comes from userAttributes if DB is not updated
            // (current user is apartment owner)
            clientContactInfo = {
                name: userAttributes.name,
                phoneNumber: userAttributes.phone_number
            };
        }
    }
    return clientContactInfo;
};
const isNumStep = (pathname: string, step: string) => pathname.split('/')[5] === 'steps' && pathname.split('/')[6] === step;
const getInitialBeforeLoginApartment = (): BeforeLoginApartment => ({
    createdAt: Date.now(),
    metadata: {
        created: true,
        createdBeforeLogin: true,
        clientContactInfoInUsersDB: true
    }
});
export { isNumStep, showWaterFee, apartmentNotReservable, hasOtherOwnerDataIfNeeded, getReservableApartmentIds, getConfirmedApartments, generateApartmentAddress, replaceMainOwnerDataFromUserObject, getApartmentOwnerEmail, getApartmentOwnerPhone, getClientContactInfo, getInitialBeforeLoginApartment };
